import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, Gift, Users, DollarSign, Copy, Share2, 
  CheckCircle2, Clock, TrendingUp, Mail, MessageCircle, Trophy, Medal, Award
} from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function Referrals() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/users/referral/code`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/referral/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/referral/leaderboard`)
      ]);

      setReferralCode(codeRes.data.referral_code);
      setShareUrl(codeRes.data.share_url);
      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent("Get 20% off TheRentPayer Rent Insurance!");
    const body = encodeURIComponent(`Hey!\n\nI've been using TheRentPayer for rent insurance and thought you might like it too.\n\nUse my referral code ${referralCode} to get 20% off your signup fee!\n\nSign up here: ${shareUrl}\n\nIt protects your rent for up to 3 months if you ever lose your job. Worth checking out!`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaSMS = () => {
    const text = encodeURIComponent(`Get 20% off TheRentPayer rent insurance with my code ${referralCode}! Sign up: ${shareUrl}`);
    window.open(`sms:?body=${text}`);
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Get 20% off TheRentPayer rent insurance with my code ${referralCode}! 🏠\n\nSign up here: ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`);
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(`I'm protected with @TheRentPayer rent insurance! Use my code ${referralCode} for 20% off 🏠🛡️`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`);
  };

  const shareViaFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(`Get 20% off TheRentPayer rent insurance with my code ${referralCode}!`)}`);
  };

  const shareViaLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Award className="h-5 w-5 text-amber-600" />;
    return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
            <Gift className="inline h-8 w-8 mr-2 text-primary" />
            Referral Program
          </h1>
          <p className="text-muted-foreground mt-2">
            Share TheRentPayer and earn rewards when friends sign up!
          </p>
        </div>

        {/* Referral Code Card */}
        <Card className="card-organic mb-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground mb-2">Your Referral Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-bold text-primary tracking-wider" data-testid="referral-code">
                  {referralCode}
                </span>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => copyToClipboard(referralCode)}
                  data-testid="copy-code-btn"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={shareViaEmail} variant="outline" className="gap-2" data-testid="share-email">
                <Mail className="h-4 w-4" />
                Email
              </Button>
              <Button onClick={shareViaSMS} variant="outline" className="gap-2" data-testid="share-sms">
                <MessageCircle className="h-4 w-4" />
                SMS
              </Button>
              <Button onClick={shareViaWhatsApp} className="gap-2 bg-green-600 hover:bg-green-700" data-testid="share-whatsapp">
                <Share2 className="h-4 w-4" />
                WhatsApp
              </Button>
              <Button 
                onClick={shareViaTwitter} 
                variant="outline" 
                className="gap-2 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-300"
                data-testid="share-twitter"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Twitter
              </Button>
              <Button 
                onClick={shareViaFacebook} 
                variant="outline" 
                className="gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                data-testid="share-facebook"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </Button>
              <Button 
                onClick={shareViaLinkedIn} 
                variant="outline" 
                className="gap-2 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                data-testid="share-linkedin"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </Button>
              <Button 
                onClick={() => copyToClipboard(shareUrl)} 
                variant="outline" 
                className="gap-2"
                data-testid="copy-link"
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <p className="text-3xl font-bold text-foreground">{stats?.total_referrals || 0}</p>
              <p className="text-sm text-muted-foreground">Total Referrals</p>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <p className="text-3xl font-bold text-foreground">{stats?.successful_referrals || 0}</p>
              <p className="text-sm text-muted-foreground">Converted</p>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <DollarSign className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
              <p className="text-3xl font-bold text-foreground">${stats?.total_rewards_earned || 0}</p>
              <p className="text-sm text-muted-foreground">Earned</p>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-amber-600" />
              <p className="text-3xl font-bold text-foreground">${stats?.pending_rewards || 0}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>

        {/* How it Works */}
        <Card className="card-organic mb-6">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
              <TrendingUp className="inline h-5 w-5 mr-2 text-primary" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">1</span>
                </div>
                <h4 className="font-semibold mb-1">Share Your Code</h4>
                <p className="text-sm text-muted-foreground">
                  Send your unique code to friends & family
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">2</span>
                </div>
                <h4 className="font-semibold mb-1">They Get 20% Off</h4>
                <p className="text-sm text-muted-foreground">
                  Your friends save on their signup fee
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">3</span>
                </div>
                <h4 className="font-semibold mb-1">You Earn $10</h4>
                <p className="text-sm text-muted-foreground">
                  Get $10 credit when they activate a policy
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="card-organic mb-6">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
              <Trophy className="inline h-5 w-5 mr-2 text-yellow-500" />
              Top Referrers Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={entry.referral_code || index}
                    className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                      entry.rank === 1 ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200' :
                      entry.rank === 2 ? 'bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200' :
                      entry.rank === 3 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200' :
                      'bg-muted/50'
                    }`}
                    data-testid={`leaderboard-rank-${entry.rank}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{entry.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {entry.successful_referrals} successful referral{entry.successful_referrals !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">${entry.rewards_earned}</p>
                      <p className="text-xs text-muted-foreground">earned</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>No referrals yet. Be the first on the leaderboard!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        {stats?.recent_referrals?.length > 0 && (
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                Recent Referrals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_referrals.map((ref, index) => (
                  <div 
                    key={ref.referral_id || index}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{ref.referred_user?.name || "User"}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge className={ref.status === "converted" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                      {ref.status === "converted" ? "Converted" : "Pending"}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Credits */}
        {stats?.credits_available > 0 && (
          <Card className="card-organic mt-6 bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-emerald-800">Available Credits</h3>
                  <p className="text-sm text-emerald-600">Apply to your next premium payment</p>
                </div>
                <p className="text-3xl font-bold text-emerald-700">${stats.credits_available}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
